import { useAuth } from '@teko/auth-react';
import { Form, Loading, styled } from '@teko/ds-desktop';
import { AddressFormType } from '@teko/ui-user';
import { UpsertAddressForm, UpsertAddressFormV2 } from '@teko/ui-user/desktop';
import { Address } from '@teko/user';
import { useServices, useTenantConfig } from 'contexts';
import { useAddressFormV2, useSuggestionAddress } from 'hooks';
import { useEffect } from 'react';

export interface NewShipmentAddressProps {
  editingAddress?: Partial<Address> | null;
  onClose: (refetch?: boolean, address?: Address) => void;
  allowAddDefaultAdress?: boolean;
}

const COUNT_STRING_WITHOUT_STREET_ADDRESS = 4;

export const NewShipmentAddress = (props: NewShipmentAddressProps) => {
  const { editingAddress, onClose, allowAddDefaultAdress } = props;
  const {
    locationsLimit,
    deliveryRequireEmail,
    epicOmni1589,
    epicOmni1825SuggestAddress,
  } = useTenantConfig();
  const { setUser, user } = useAuth();
  const { userService, locationService } = useServices();
  const [form] = Form.useForm<AddressFormType>();

  const {
    message,
    isDefault,
    setIsDefault,
    submitForm,
    loading: loadingButton,
    provinces,
    wards,
    setProvince,
    setWard,
    setPlace,
    province,
    ward,
    setLocation,
  } = useAddressFormV2({
    editingAddress,
    requireEmail: deliveryRequireEmail,
    allowAddDefaultAddress: allowAddDefaultAdress,
  });

  const resetPosition = () => {
    setPlace({
      lat: undefined,
      long: undefined,
    });
  };

  const {
    dataSuggestion,
    setSuggestInputText,
    openListSuggestion,
    setOpenListSuggestion,
    setProvince: setProvinceSuggest,
    setDistrict: setDistrictSuggest,
    setWard: setWardSuggest,
    isLoadingSuggest,
    getPlaceDetails,
  } = useSuggestionAddress({
    province: province?.title || editingAddress?.provinceName || '',
    district: editingAddress?.districtName || '',
    ward: ward?.title || editingAddress?.wardName || '',
  });

  useEffect(() => {
    if (editingAddress && epicOmni1589) {
      form.setFieldsValue({
        name: editingAddress.name,
        telephone: editingAddress.telephone,
        email: editingAddress.email || '',
        provinceCode: editingAddress?.provinceCode,
        wardCode: editingAddress?.wardCode,
        address: editingAddress.address,
      });
      setIsDefault(!!editingAddress.isDefault);
      setPlace({
        lat: !!editingAddress.latitude
          ? Number(editingAddress.latitude)
          : undefined,
        long: !!editingAddress.longitude
          ? Number(editingAddress.longitude)
          : undefined,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editingAddress]);

  const onClickSuggest = async (placeId: string) => {
    const items = await getPlaceDetails(placeId);
    if (!items || !Array.isArray(items) || !items.length) {
      return;
    }
    // items là danh sách các placeDetail và cần lấy placeDetail đầu tiên
    // trong case này chỉ gửi 1 placeID lên nên chỉ có 1 placeDetail
    const item = items?.[0];
    const address =
      item.fullAddress
        .split(',')
        ?.splice(
          0,
          item.fullAddress.split(',').length -
            COUNT_STRING_WITHOUT_STREET_ADDRESS,
        )
        ?.join(',') ||
      item.fullAddress ||
      '';
    setOpenListSuggestion(false);
    setPlace({ lat: item.lat, long: item.long });
    const newValue = {
      provinceCode: item.provinceCode || undefined,
      ...(item.wardCode ? { wardCode: item.wardCode } : {}),
    };
    form.setFieldsValue({
      address,
      ...newValue,
    });
    if (
      !!item.provinceId &&
      !!item.wardId &&
      !Number.isNaN(Number(item.provinceId)) &&
      !Number.isNaN(Number(item.wardId))
    ) {
      setLocation({
        provinceId: item.provinceCode,
        wardId: item.wardCode,
      });
    }
  };

  return (
    <ScrollView>
      {epicOmni1589 ? (
        <Loading isLoading={loadingButton}>
          <StyledUpsertAddressFormV2
            message={message}
            provinces={provinces}
            wards={wards}
            form={form}
            hideFormTitle
            requireEmail={deliveryRequireEmail}
            defaultCheckboxJustify="end"
            onCloseForm={(refetch, newAddress) => {
              onClose(refetch, newAddress);
              if (refetch && !!user && !!newAddress && newAddress.isDefault) {
                setUser({
                  ...user,
                  address: newAddress,
                });
              }
            }}
            submitForm={submitForm}
            isDefault={isDefault}
            setIsDefault={setIsDefault}
            onChangeProvince={(province) => {
              setProvince(province);
              resetPosition();
            }}
            onChangeWard={(ward) => {
              setWard(ward);
              resetPosition();
            }}
            dataSuggestion={dataSuggestion}
            openListSuggestion={openListSuggestion}
            onChangeAddress={(text) => {
              setSuggestInputText(text);
              setOpenListSuggestion(true);
              resetPosition();
            }}
            onClickSuggest={onClickSuggest}
            isLoadingSuggest={isLoadingSuggest}
          />
        </Loading>
      ) : (
        <StyledUpsertAddressForm
          locationService={locationService}
          userService={userService}
          form={form}
          requireEmail={deliveryRequireEmail}
          editingAddress={editingAddress}
          onCloseForm={(refetch, newAddress) => {
            onClose(refetch, newAddress);
            // update default address for user
            if (refetch && !!user && !!newAddress && newAddress.isDefault) {
              setUser({
                ...user,
                address: newAddress,
              });
            }
          }}
          locationsLimit={locationsLimit}
          defaultCheckboxJustify="end"
          customOnProvinceChanged={(province) => {
            setProvinceSuggest(province?.name || '');
            setDistrictSuggest('');
            setWardSuggest('');
            resetPosition();
          }}
          customOnDistrictChanged={(district) => {
            setDistrictSuggest(district?.name || '');
            setWardSuggest('');
            resetPosition();
          }}
          customOnWardChanged={(ward) => {
            setWardSuggest(ward?.name || '');
            resetPosition();
          }}
          hideFormTitle
          allowAddDefaultAdress={allowAddDefaultAdress}
          dataSuggestion={dataSuggestion}
          openListSuggestion={openListSuggestion}
          onChangeAddress={(text) => {
            setSuggestInputText(text);
            setOpenListSuggestion(true);
            resetPosition();
          }}
          setOpenListSuggestion={setOpenListSuggestion}
          isLoadingSuggest={isLoadingSuggest}
          isDisableFullAddressWhenNotOtherInfo={epicOmni1825SuggestAddress}
          getPlaceDetails={getPlaceDetails}
        />
      )}
    </ScrollView>
  );
};

const ScrollView = styled.div`
  overflow-y: auto;
  height: 75vh;
  padding-right: 4px;

  ::-webkit-scrollbar-track {
    -webkit-box-shadow: inset 0 0 6px rgba(0, 0, 0, 0.3);
    background-color: #f5f5f5;
  }

  ::-webkit-scrollbar {
    width: 4px;
    background-color: #f5f5f5;
  }

  ::-webkit-scrollbar-thumb {
    background-color: #999;
  }
`;

const StyledUpsertAddressForm = styled(UpsertAddressForm)`
  margin-bottom: 50px;
`;

const StyledUpsertAddressFormV2 = styled(UpsertAddressFormV2)`
  margin-bottom: 50px;
`;
